/**
 * The practice driver: the shared runner stepped locally on a fixed-step accumulator, with
 * prev/curr interpolation for both mechs and every projectile (the Phase 3 behaviour).
 */
import type {
  Deployable,
  MatchEvent,
  MatchSetup,
  MatchState,
  Slot,
} from '@steelwake/shared/combat';
import type { ArenaRuntime } from '@steelwake/shared/arena';
import { ActiveBalanceSchema } from '@steelwake/shared/data';
import type { Loadout } from '@steelwake/shared/loadout';
import { HangarSchema } from '@steelwake/shared/progression';

import { api } from '../../api.js';
import { useAppStore, type AuthStatus } from '../../store.js';
import type { InputSample } from '../sim/input.js';
import { advance } from '../sim/loop.js';
import type {
  DriverContext,
  MatchDescriptor,
  MatchDriver,
  MatchSource,
  MechFrame,
  PracticeHooks,
  PrepareContext,
  ProjectileView,
} from './driver.js';
import { MatchSession } from './session.js';
import type { MatchParams } from './setup.js';

export class LocalMatchDriver implements MatchDriver {
  readonly mySlot: Slot = 0;
  readonly enemySlot: Slot = 1;
  readonly practice: PracticeHooks;
  private acc = 0;
  private alpha = 1;

  constructor(private readonly session: MatchSession) {
    const snap = (change: () => void): void => {
      change();
      // The session's previous state equals its current one after a hook: draw it exactly.
      this.acc = 0;
      this.alpha = 1;
    };
    this.practice = {
      get params() {
        return session.setup;
      },
      setBotLevel: (level) => snap(() => session.setBotLevel(level)),
      skipCountdown: () => snap(() => session.skipCountdown()),
      setRoundSeconds: (seconds) => snap(() => session.setRoundSeconds(seconds)),
      ultimateReady: () => snap(() => session.ultimateReady()),
      rematch: (seed) => snap(() => session.rematch(seed)),
      teleport: (position) => snap(() => session.teleport(position)),
    };
  }

  /** One input sample per frame, latched into every fixed step it covers (DECISIONS.md #16). */
  advance(frameMs: number, sample: InputSample): MatchEvent[] {
    const adv = advance(this.acc, frameMs);
    this.acc = adv.acc;
    this.alpha = adv.alpha;
    const events: MatchEvent[] = [];
    for (let i = 0; i < adv.steps; i++) events.push(...this.session.step(sample));
    return events;
  }

  get state(): MatchState {
    return this.session.state;
  }

  get ownState(): MatchState {
    return this.session.state;
  }

  mech(slot: Slot): MechFrame {
    return { pose: this.session.pose(slot, this.alpha), combatant: this.session.state.mechs[slot] };
  }

  projectiles(): ProjectileView[] {
    return this.session.projectiles(this.alpha);
  }

  features(): ArenaRuntime {
    return this.session.state.features;
  }

  viewTick(): number {
    return this.session.state.tick - 1 + this.alpha;
  }

  deployables(): readonly Deployable[] {
    return this.session.state.deployables;
  }

  get finished(): boolean {
    return this.session.finished;
  }

  dispose(): void {
    // The session holds no resources of its own; the world belongs to the game.
  }
}

/** A practice match described by its URL parameters (DECISIONS.md #85). */
export class PracticeSource implements MatchSource {
  private loadouts: [Loadout | null, Loadout | null] = [null, null];

  private balance: MatchSetup['balance'] = undefined;

  constructor(private readonly params: MatchParams) {}

  /**
   * A signed-in pilot practises with the loadout saved for the mech (owned items only, as the
   * server validated when it was saved); anyone else, and the bot, use the defaults.
   */
  async prepare(signal: AbortSignal, _ctx: PrepareContext): Promise<MatchDescriptor> {
    const [loadout, balance] = await Promise.all([
      savedLoadout(this.params.mech, signal),
      publishedBalance(signal),
    ]);
    this.loadouts = [loadout, null];
    this.balance = balance;
    return {
      mechs: [this.params.mech, this.params.opponent],
      mySlot: 0,
      arenaId: this.params.arena,
      arenaHash: null,
      loadouts: this.loadouts,
      balanceVersion: balance?.version ?? 0,
    };
  }

  createDriver(ctx: DriverContext): MatchDriver {
    return new LocalMatchDriver(
      new MatchSession({
        params: this.params,
        arena: ctx.arena,
        world: ctx.world,
        e2e: ctx.e2e,
        loadouts: this.loadouts,
        ...(this.balance ? { balance: this.balance } : {}),
      }),
    );
  }
}

/** How long practice waits for the balance document before starting on the built-in data. */
export const BALANCE_FETCH_MS = 1000;

/**
 * The published balance document (Phase 7), so practice plays by the same numbers as online.
 * A slow or absent API never stalls the match: after a second the built-in data wins.
 */
async function publishedBalance(signal: AbortSignal): Promise<MatchSetup['balance']> {
  const timeout = new Promise<undefined>((resolve) => {
    const timer = setTimeout(() => resolve(undefined), BALANCE_FETCH_MS);
    signal.addEventListener('abort', () => clearTimeout(timer), { once: true });
  });
  const fetched = (async (): Promise<MatchSetup['balance']> => {
    try {
      const doc = ActiveBalanceSchema.parse(await api<unknown>('/api/v1/config/balance'));
      return doc.version > 0 ? { version: doc.version, overrides: doc.overrides } : undefined;
    } catch {
      return undefined;
    }
  })();
  return Promise.race([fetched, timeout]);
}

/** The session check's answer, waited for briefly when the page has only just loaded. */
function authKnown(signal: AbortSignal): Promise<AuthStatus> {
  const now = useAppStore.getState().authStatus;
  if (now !== 'unknown') return Promise.resolve(now);
  return new Promise((resolve) => {
    let settled = false;
    const done = (status: AuthStatus) => {
      if (settled) return;
      settled = true;
      unsubscribe();
      clearTimeout(timer);
      resolve(status);
    };
    const unsubscribe = useAppStore.subscribe((state) => {
      if (state.authStatus !== 'unknown') done(state.authStatus);
    });
    const timer = setTimeout(() => done(useAppStore.getState().authStatus), 3000);
    signal.addEventListener('abort', () => done('anonymous'), { once: true });
  });
}

/** The account's saved loadout for `mechId`, or null when signed out, unsaved or unreachable. */
async function savedLoadout(mechId: string, signal: AbortSignal): Promise<Loadout | null> {
  if ((await authKnown(signal)) !== 'authenticated') return null;
  try {
    const hangar = HangarSchema.parse(await api<unknown>('/api/v1/hangar'));
    if (signal.aborted) return null;
    return hangar.loadouts[mechId] ?? null;
  } catch {
    // Signed out, or the API is away: practice still runs, on the stock loadout.
    return null;
  }
}

/** Identifies a practice setup; any change remounts the game. */
export function practiceKey(p: MatchParams): string {
  return [
    'practice',
    p.arena,
    p.mech,
    p.opponent,
    p.bot,
    p.seed,
    p.roundSeconds,
    p.countdownSeconds,
    p.roundsToWin,
    p.ultReady,
  ].join('|');
}
